/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/**
 * Client Dimension Interface.
 */
export interface IClientDimension {
    width: number;
    height: number;
}

/**
 * Dictionary String Interface.
 */
export interface IDictionaryStringString {
    [key: string]: string;
}

/**
 * @interface IGrid
 * @classdesc - The IGrid interface contract.
 */
export interface IGrid {
    rows: number;
    columns: number;
}

/**
 * Clamp position between a range.
 * @param value - Value to be clamped.
 * @param min - Minimum value in range.
 * @param max - Maximum value in range.
 * @returns The clap value if min passed is less than max else returns 0.
 */
export function clamp(value: number, min: number, max: number): number {
    if (min <= max) {
        return Math.min(Math.max(value, min), max);
    }
    return 0;

}

/**
 * Upper case the first letter of the string, if only one char exists on the string, uppercase it.
 * @param value - Value to be upper cased.
 * @returns The upper cased string of the passed value, if it has just one char uppercase it.
 */
export function toUpperCaseFirstChar(value: string): string {
    if (value.length > 1) {
        return value.charAt(0).toUpperCase() + value.substr(1);
    }
    if (value.length === 1) {
        return value.charAt(0).toUpperCase();
    }
    return '';

}

/**
 * HTML collectio to Array.
 * @param nodeList - Html Collection.
 * @returns - An array of the HTML Element.
 */
export function htmlCollectionToArray(nodeList: HTMLCollection): HTMLElement[] {
    return Array.prototype.slice.call(nodeList);
}

/**
 * Get CSS for the Element.
 * @param element - The HTML Element.
 * @param property - The HTML property.
 * @returns - The value of the required property.
 */
export function getCSS(element: HTMLElement, property: string): string {
    let style = element.style[property];

    if (!style) {
        style = getComputedStyle(element)[property];
    }

    return style;
}

/**
 * Create Throttled Event.
 * @param listener - The Event Listener.
 * @param threshold - The threshold number.
 * @returns The event.
 */
export function createThrottledEvent(listener: EventListener, threshold: number):
(event: Event) => void {
    let timer: any;
    let last = 0;

    return (event: Event) => {
        const now = Date.now();

        if (timer) {
            clearTimeout(timer);
            timer = undefined;
        }

        if (last && (now < last + threshold)) {
            timer = setTimeout(() => {
                last = Date.now();
                listener(event);
            },
            threshold - (now - last));
        } else {
            last = now;
            listener(event);
        }
    };
}

/**
 * Add the Throrrled Event.
 * @param target - Event Targer.
 * @param name - Name of the Event.
 * @param listener - The Event Listener.
 * @param threshold - The Threshold number.
 * @returns - Returns the Throttled Event.
 */
export function addThrottledEvent(target: EventTarget, name: string, listener: EventListener, threshold: number = 150):
(event: Event) => void {

    const throttledEventHandler = createThrottledEvent(listener, threshold);
    target.addEventListener(name, throttledEventHandler, false);

    return throttledEventHandler;
}

/**
 * Map the CSS to the modules.
 * @param className - Class name.
 * @param cssModule - CSS modules.
 * @returns - Returns the map CSS.
 */
export function mapToCssModules(className: string = '', cssModule: any): string {
    if (!cssModule) {
        return className;
    }

    return className
        .split(' ')
        .map((c: string) => cssModule[c] || c)
        .join(' ');
}

/**
 * Check the raget object is reference.
 * @param target - Target object.
 * @returns Returns boolean.
 */
export function isRef(target: any): boolean {
    if (!target) {
        return false;
    }

    return (<React.RefObject<HTMLElement>>target).current !== undefined;
}

/**
 * Check if the passed target is element.
 * @param target - Target element.
 * @returns Returns boolean.
 */
export function isElement(target: any): boolean {
    if (!target) {
        return false;
    }

    return (<HTMLElement>target).addEventListener !== undefined;
}

/**
 * KeyCodes Enum.
 */
export const keyCodes = {
    esc: 27,
    space: 32,
    tab: 9,
    up: 38,
    down: 40,
    enter: 13
};

/**
 * Map Interface.
 */
interface IMap<T> {
    [key: string]: T;
}
const warned: IMap<boolean> = {};

/**
 * Warn once.
 * @param message - Message to warned.
 */
export function warnOnce(message: string): void {
    if (!warned[message]) {
        if (typeof console !== 'undefined') {
            throw new Error(message);
        }
        warned[message] = true;
    }
}

/**
 * Can use DOM..
 */
export const canUseDOM = !!(
    typeof window !== 'undefined' &&
        window.document &&
        window.document.createElement
);

/**
 * Find the required DOM element.
 * @param target - Targert Dom Element.
 * @returns - The DOM Element or string or null.
 */
export function findDOMElements(target: string): NodeListOf<Element> | string | null {
    try {
        if (typeof target === 'string' && canUseDOM) {
            let selection = document.querySelectorAll(target);

            if (!selection.length) {
                selection = document.querySelectorAll(`#${target}`);
            }

            if (!selection.length) {
                throw new Error();
            }
            return selection;
        }

        return target;
    } catch {
        console.error(`The target '${target}' could not be identified in the dom, tip: check spelling`);
        return null;
    }
}

/**
 * Get the Target element.
 * @param target - Target Element.
 * @returns - Returns the HTMLElement or null or string.
 */
export function getTarget(target: string | HTMLElement | React.Ref<HTMLElement>): HTMLElement | React.Ref<HTMLElement> | null | string {
    const selection = findDOMElements(<string>target);
    if (selection && selection.length) {
        return <HTMLElement>selection[0];
    }

    return target;
}

/**
 * Get the Target element.
 * @param target - Target Element.
 * @returns - Returns the HTMLElement or null or string.
 */
export function getTargetElement(target: string | HTMLElement | React.Ref<HTMLElement>): HTMLElement | null {
    const targetElement = getTarget(target);
    if (isRef(targetElement)) {
        return (<React.RefObject<HTMLElement>>targetElement).current;
    }
    if (isElement(targetElement)) {
        return <HTMLElement>targetElement;
    }

    return null;
}

/**
 * Transition Timeouts.
 */
export const TransitionTimeouts = {
    Fade: 150,
    Collapse: 350,
    Modal: 300,
    Carousel: 600
};

/**
 * Transition Statuses.
 */
export type TransitionStatuses = 'entering' | 'entered' | 'exiting' | 'exited';

/**
 * Focusable Elements.
 */
export const focusableElements = [
    'a[href]',
    'area[href]',
    'input:not([disabled]):not([type=hidden])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'button:not([disabled])',
    'iframe',
    'object',
    'embed',
    '[tabindex]:not(.modal)',
    'audio[controls]',
    'video[controls]',
    '[contenteditable]:not([contenteditable="false"])'
];

/**
 * Extends an object with properties/values from one or more others.
 * @param parameters - The objects to merge together.
 * @returns - The extended object.
 */
export function extend(...parameters: any[]): any {
    if (!parameters || !parameters.length) {
        return null;
    }

    const recursive = (typeof (parameters[0]) === 'boolean') && parameters[0];

    if (parameters.length < 2) {
        return recursive ? null : parameters[0];
    }

    if (recursive && (parameters.length < 3)) {
        return parameters[1];
    }

    const target = recursive ? parameters[1] : parameters[0];

    for (let paramIndex = recursive ? 2 : 1; paramIndex < parameters.length; paramIndex++) {
        if (parameters[paramIndex] !== undefined) {
            for (const key of Object.keys(parameters[paramIndex])) {
                if (parameters[paramIndex].hasOwnProperty(key)) {
                    const source = parameters[paramIndex][key];

                    if (recursive) {
                        let isSourceArray = Array.isArray ?
                            Array.isArray(source) :
                            ({}).toString.call(source) === '[object Array]';
                        let isTargetArray = !!target[key] && (Array.isArray ?
                            Array.isArray(target[key]) :
                            ({}).toString.call(target[key]) === '[object Array]');
                        let isSourceObject = !isSourceArray && (typeof source === 'object');
                        let isTargetObject = !isTargetArray && !!target[key] && (typeof target[key] === 'object');

                        if (isSourceArray && isTargetArray) {
                            for (const [arrayIndex, element] of source.entries()) {
                                isSourceArray = Array.isArray ?
                                    Array.isArray(element) :
                                    ({}).toString.call(element) === '[object Array]';
                                isTargetArray = !!target[key][arrayIndex] && (Array.isArray ?
                                    Array.isArray(target[key][arrayIndex]) :
                                    ({}).toString.call(target[key][arrayIndex]) === '[object Array]');
                                isSourceObject = !isSourceArray && (typeof element === 'object');
                                isTargetObject = !isTargetArray && !!target[key][arrayIndex] && (typeof target[key][arrayIndex] === 'object');

                                if (isSourceArray) {
                                    target[key][arrayIndex] = extend(true, isTargetArray ? target[key][arrayIndex] : [], element);
                                } else if (isSourceObject) {
                                    target[key][arrayIndex] = extend(true, isTargetObject ? target[key][arrayIndex] : {}, element);
                                } else {
                                    target[key][arrayIndex] = element;
                                }
                            }
                            continue;
                        } else if (isSourceArray) {
                            target[key] = extend(true, [], source);
                            continue;
                        } else if (isSourceObject) {
                            target[key] = extend(true, isTargetObject ? target[key] : {}, source);
                            continue;
                        }
                    }

                    target[key] = source;
                }
            }
        }
    }

    return target;
}

/**
 * Gets the dimension of an element.
 * @param containerElement - The container element.
 * @returns - {IClientDimension} - The client dimension.
 * {IClientDimension.width} - The client width.
 * {IClientDimension.height} - The client height.
 */
export function getDimensions(containerElement: HTMLElement): IClientDimension | null {
    if (containerElement === null) {
        return null;
    }
    return {
        width: containerElement.clientWidth,
        height: containerElement.clientHeight
    };
}

/**
 * Check if the browser is Safari or not.
 * @returns Ture or false.
 */
export function isSafari(): boolean {
    if (navigator.vendor && navigator.userAgent) {
        return navigator.vendor.includes('Apple') &&
            !navigator.userAgent.includes('CriOS') &&
            !navigator.userAgent.includes('FxiOS');
    }
    return false;
}
